import { type PublicPluginAPI } from '@atlaskit/editor-common/types';
import { createDefaultPreset } from '@atlaskit/editor-core/preset-default';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { type FocusPlugin } from '@atlaskit/editor-plugin-focus';
import { gridPlugin, type GridPlugin } from '@atlaskit/editor-plugin-grid';
import { listPlugin, type ListPlugin } from '@atlaskit/editor-plugin-list';
import {
	maxContentSizePlugin,
	type MaxContentSizePlugin,
} from '@atlaskit/editor-plugin-max-content-size';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import { contentInsertionPlugin } from '@atlaskit/editor-plugins/content-insertion';
import { datePlugin } from '@atlaskit/editor-plugins/date';
import { expandPlugin } from '@atlaskit/editor-plugins/expand';
import { helpDialogPlugin } from '@atlaskit/editor-plugins/help-dialog';
import { insertBlockPlugin } from '@atlaskit/editor-plugins/insert-block';
import { rulePlugin } from '@atlaskit/editor-plugins/rule';
import { statusPlugin } from '@atlaskit/editor-plugins/status';
import { tasksAndDecisionsPlugin } from '@atlaskit/editor-plugins/tasks-and-decisions';
import { textColorPlugin } from '@atlaskit/editor-plugins/text-color';
import { toolbarListsIndentationPlugin } from '@atlaskit/editor-plugins/toolbar-lists-indentation';

export const MAX_CHARACTERS_INPUT = 16000;

type EditorPresetResult = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	preset: any;
	editorApi?: PublicPluginAPI<
		[MaxContentSizePlugin, GridPlugin, QuickInsertPlugin, ListPlugin, FocusPlugin]
	>;
};

const buildPreset = (placeholder: string) =>
	createDefaultPreset({
		paste: { sanitizePrivateContent: true },
		placeholder: { placeholder },
		blockType: {
			allowBlockType: {
				exclude: ['heading'],
			},
		},
		allowUndoRedoButtons: true,
	})
		.add([maxContentSizePlugin, MAX_CHARACTERS_INPUT])
		.add(gridPlugin)
		.add([expandPlugin, { allowInsertion: true }])
		.add(rulePlugin)
		.add(tasksAndDecisionsPlugin)
		.add(datePlugin)
		.add(statusPlugin)
		.add(textColorPlugin)
		.add([helpDialogPlugin, true])
		.add(listPlugin)
		.add([
			toolbarListsIndentationPlugin,
			{ allowHeadingAndParagraphIndentation: true, showIndentationButtons: false },
		])
		.add(insertBlockPlugin)
		.add(contentInsertionPlugin);

export const useEditorPreset = (placeholder: string): EditorPresetResult => {
	const { preset, editorApi } = usePreset(() => buildPreset(placeholder), []);

	return { preset, editorApi };
};
